
import assign from 'lodash/assign';
import VideoJS from 'video.js';
import Hls from 'hls.js';
import isMobile from 'ismobilejs';

import * as ActionCreators from './actions/ActionCreators';
import { configureStore } from './store';
import { AS_PLAYER_DEFAULTS } from './player/defaults/ASPlayer';
import { VIDEO_JS_DEFAULTS } from './player/defaults/VideoJS';
import ADBP from './tracking/ADBP';
import ASDebugger from './utils/ASDebugger';
import Freewheel from './ads/Freewheel';
import HlsJSSourceHandler from './player/HlsJSSourceHandler';

import './player/components/ASAdCountdown';
import './player/components/ASCaptionsButton';
import './player/components/ASCloseButton';
import './player/components/ASFullscreenToggle';
import './player/components/ASMutedOverlayButton';
import './player/components/ASMuteToggle';
import './player/components/ASPlayToggle';
import './player/components/ASReturnControl';
import './player/components/ASShareButton';
import './player/components/ASShareOverlay';
import './player/components/ASSubtitlesButton';
import './player/plugins/DetectKeyboardFocus';

export class Player {
	constructor(elementID = 'video-player', videoID, options = {}) {
	    if (typeof elementID !== 'string') {
	        console.warn('The first argument for ASVP.Player should be a string of the ID of the video HTML element.  Defaulting to \'video-player\'');
	        elementID = 'video-player';
		}

		// allows for new Player(element, 'id', {}) as well as new Player(element, {})
		if (arguments.length === 2) {
			options = videoID;
		}

		// if using all three args, set the videoID in options to the second arg
		if (arguments.length === 3) {
			if (typeof arguments[1] === 'string') options.videoID = videoID;
		}

	    this._elementID = elementID;
	    this._options = assign({}, AS_PLAYER_DEFAULTS, options);

	    this._videoElement = document.getElementById(this._elementID);

	    if (this._videoElement.tagName !== 'VIDEO') {
	        console.error('Element for ASVP.Player must be a video tag.');
	        return false;
	    }

	    this._debug = ASDebugger(this._options.debug || false);

	    this._originalVideoTagParent = document.getElementById(this._elementID).parentNode; // get the original parent of the video element
	    this._originalVideoTag = document.getElementById(this._elementID).cloneNode(true); // clone the original video element and it's children

	    // attaches hls.js as source handler for html playback
	    this._attachHlsSourceHandler();

	    const videoJSOptions = assign({}, VIDEO_JS_DEFAULTS, this._options.videoJS || {});
        this._player = VideoJS(this._elementID, videoJSOptions);
        this._player.detectKeyboardFocus();

	    this._player.addClass('adult-swim-video-player');
	    this._player.addClass(isMobile.any ? 'mobile' : 'not-mobile');
	    this._player.addClass(isMobile.android.device ? 'mobile--android' : 'not-mobile--android');
	    this._player.addClass(isMobile.apple.device ? 'mobile--apple' : 'not-mobile--apple');
	    this._player.addClass(isMobile.phone ? 'mobile--phone' : 'not-mobile--phone');
	    this._player.addClass(isMobile.tablet ? 'mobile--tablet' : 'not-mobile--tablet');

	    this._ads = (this._options.ads) ? new Freewheel(this._player, this._options.ads) : null;

	    this._store = configureStore(this._getInitialState(), {
	    	player: this._player,
	    	options: this._options,
	    	debug: this._debug,
	    	analytics: ADBP,
	    	ads: this._ads
	    }, this._options.debug);
	    this._store.dispatch(ActionCreators.init());
	}

	_getInitialState() {
        return {};
    }

    // if hls is supported through hls.js (and doesn't have native hls), attach HLJ.js as source handler on VideoJS
    _attachHlsSourceHandler() {
    	if (Hls.isSupported()) {
	        VideoJS.getTech('Html5').registerSourceHandler({
	            canHandleSource: (source) => {
	                const hlsTypeRE = /^application\/x-mpegURL$/i;
	                const hlsExtRE = /\.m3u8/i;
	                let result;

	                if (hlsTypeRE.test(source.type)) {
	                    result = 'probably';
	                } else if (hlsExtRE.test(source.src)) {
	                    result = 'maybe';
	                } else {
	                    result = '';
	                }

	                // if there's a native hls player, use it
	                if (document.createElement('video').canPlayType('application/vnd.apple.mpegURL')) result = false;

	                return result;
	            },

	            handleSource: (source, tech) => {
	                if (tech.hlsProvider) tech.hlsProvider.dispose();

	                tech.hlsProvider = new HlsJSSourceHandler(source, tech, this._options.hlsJS);

	                return tech.hlsProvider;
	            }

	        }, 0);
	    }
	}

	// registers player event listening
	// use html5 video player event names - progress, loadstart, end, etc
	on(eventName, callback) {
	    if (arguments.length !== 2) {
	        console.error('Missing one or more arguments in registerEvent call.  Needs both eventName and callback function in that order.');
	        return false;
	    }

	    this._player.on(eventName, callback);

    	return this.off.bind(this, eventName, callback);
	}
	// alias for on
	registerEvent(eventName, callback) {
		return this.on(eventName, callback);
	}

	/**
     * deregisters player event listening
     */
	off(eventName, func) {
	    if (!arguments.length) {
	        console.error('Missing a required argument in deregisterEvent call.  Needs eventName passed in.');
	        return false;
	    }

	    this._player.off(eventName, func);
	}
	// alias for off
	deregisterEvent(eventName, func) {
		this.off(eventName, func);
	}


	/**
	 * returns the video.js player instance for manipulating playback methods
	 */
	getPlayer() {
		if (!this._player) {
			console.error('ASVideoPlayer not created yet.  Cannot retrieve player.');
			return false;
		}

		return this._player;
    }

    share() {
		const action = this._store.getState().player.share ? ActionCreators.onShareControlClose() : ActionCreators.onShareControlClick();
		this._store.dispatch(action);
    }

	/* 	videoID (string) , accessToken (object)
		use to set a new video (using unicron ids)
		will clear out current video and request new one
		can optionally set accessToken before playback */
	setVideo(videoID, accessToken) {
	    if (!arguments.length) {
	        console.error('No arguments passed to setVideo.  It needs a unicron video ID to retrieve video assets.');
	        return false;
	    }

		if (accessToken) this._store.dispatch(ActionCreators.receiveAccessToken(accessToken));
	    this._store.dispatch(ActionCreators.setVideoID(videoID));
	}


	/* 	accessToken (object)
		use to set a new access token */
	setAccessToken(accessToken) {
		if (!arguments.length) {
			console.error('No arguments passed to setAccessToken.  It needs a token object to set properly.');
			return false;
		}

		this._store.dispatch(ActionCreators.receiveAccessToken(accessToken));
	}

	// enables ads dynamically after initialization
	enableAds() {
		this._store.dispatch(ActionCreators.enableAds());
	}

	// disables ads from playing
	disableAds() {
		this._store.dispatch(ActionCreators.disableAds());
	}

	// destroys everything - video.js, events, video tag
	destroy(destroyVideoTag = true) {
	    // destroy hls.js and video.js
	    if (this._player.tech_) this._player.tech_.dispose();
	    this._player.dispose();

	    if (!destroyVideoTag) {
	        this._originalVideoTagParent.appendChild(this._originalVideoTag);
	    }

	    this._store.dispatch(ActionCreators.destroy());
	}
}



// WEBPACK FOOTER //
// ./src/Player.js